import { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Banner,
  Button,
  Card,
  Checkbox,
  IndexTable,
  Page,
  Text,
  TextField,
} from '@shopify/polaris';

import { api } from '@/api/client';

interface Shop {
  domain: string;
  timezone: string | null;
  country: string | null;
  currency: string | null;
  installed_at: number;
  uninstalled_at: number | null;
  billing_status: string | null;
  billing_plan: string | null;
  is_test: number;
}

/** Where a shop-login token takes you — the merchant admin, in standalone mode. */
const ADMIN_BASE = import.meta.env.VITE_ADMIN_BASE_URL ?? '';

export function ShopsPage() {
  const [shops, setShops] = useState<Shop[]>([]);
  const [q, setQ] = useState('');
  const [includeTest, setIncludeTest] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (includeTest) params.set('includeTest', 'true');
    void api<{ shops: Shop[] }>(`/ops/api/shops?${params}`)
      .then((r) => setShops(r.shops ?? []))
      .catch((e: Error) => setError(e.message));
  }, [q, includeTest]);

  useEffect(load, [load]);

  /**
   * ⚠ Opening a merchant's own admin as them.
   *
   * The token is minted server-side, logged with the acting admin, lasts 24
   * hours and cannot be refreshed. It goes in the URL because that is how
   * standalone mode receives it — and standalone immediately moves it to
   * sessionStorage and scrubs it from the address bar, so it does not linger in
   * history or in a referrer.
   */
  const openAs = useCallback(async (domain: string) => {
    setBusy(domain);
    setError(null);
    try {
      const { token } = await api<{ token: string }>(
        `/ops/api/shops/${encodeURIComponent(domain)}/login-token`,
        { method: 'POST' }
      );
      window.open(`${ADMIN_BASE}/?token=${encodeURIComponent(token)}`, '_blank', 'noopener');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }, []);

  return (
    <Page title="Shops" subtitle={`${shops.length} shown`}>
      {error && (
        <Banner tone="critical" title="Could not load shops" onDismiss={() => setError(null)}>
          <p>{error}</p>
        </Banner>
      )}
      <Card>
        <TextField
          label="Search"
          labelHidden
          value={q}
          onChange={setQ}
          placeholder="Filter by domain"
          autoComplete="off"
          clearButton
          onClearButtonClick={() => setQ('')}
        />
        <Checkbox label="Include test stores" checked={includeTest} onChange={setIncludeTest} />
        <IndexTable
          resourceName={{ singular: 'shop', plural: 'shops' }}
          itemCount={shops.length}
          selectable={false}
          headings={[
            { title: 'Shop' },
            { title: 'Plan' },
            { title: 'Timezone' },
            { title: 'Status' },
            { title: '' },
          ]}
        >
          {shops.map((s, i) => (
            <IndexTable.Row id={s.domain} key={s.domain} position={i}>
              <IndexTable.Cell>
                <Text as="span" fontWeight="semibold">
                  {s.domain}
                </Text>
                {s.is_test ? <Badge>Test</Badge> : null}
              </IndexTable.Cell>
              <IndexTable.Cell>
                {s.billing_status === 'ACTIVE' ? (
                  <Badge tone="success">{s.billing_plan ?? 'Pro'}</Badge>
                ) : (
                  <Text as="span" tone="subdued">
                    Free
                  </Text>
                )}
              </IndexTable.Cell>
              <IndexTable.Cell>{s.timezone ?? '—'}</IndexTable.Cell>
              <IndexTable.Cell>
                {s.uninstalled_at ? (
                  <Badge tone="critical">Uninstalled</Badge>
                ) : (
                  <Badge tone="success">Installed</Badge>
                )}
              </IndexTable.Cell>
              <IndexTable.Cell>
                <Button
                  size="slim"
                  loading={busy === s.domain}
                  disabled={!!s.uninstalled_at}
                  onClick={() => void openAs(s.domain)}
                >
                  Open as merchant
                </Button>
              </IndexTable.Cell>
            </IndexTable.Row>
          ))}
        </IndexTable>
      </Card>
    </Page>
  );
}
